import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { 
  ArrowLeft, Check, Crown, Clock, CreditCard, 
  Plane, BookOpen, Target, Award, Loader2
} from "lucide-react";

const SubscriptionPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  // Check for returning from Stripe
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    fetchSubscriptionStatus();
  }, []);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [sessionId]);

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`, { withCredentials: true });
      setSubscription(response.data);
    } catch (error) {
      console.error("Error fetching subscription:", error);
      toast.error("Erro ao carregar estado da subscrição");
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (checkoutSessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error("Timeout ao verificar pagamento. Por favor, verifique o email para confirmação.");
      return;
    }

    try {
      const response = await axios.get(
        `${API}/subscription/checkout/status/${checkoutSessionId}`,
        { withCredentials: true }
      );

      if (response.data.payment_status === "paid") {
        toast.success("Pagamento confirmado! Subscrição ativada.");
        fetchSubscriptionStatus();
        // Clear URL params
        navigate("/subscription", { replace: true });
        return;
      } else if (response.data.status === "expired") {
        toast.error("Sessão de pagamento expirada. Por favor, tente novamente.");
        navigate("/subscription", { replace: true });
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(checkoutSessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error("Error checking payment status:", error);
      setTimeout(() => pollPaymentStatus(checkoutSessionId, attempts + 1), pollInterval);
    }
  };

  const handleSubscribe = async () => {
    setProcessing(true);
    try {
      const response = await axios.post(
        `${API}/subscription/checkout`,
        { origin_url: window.location.origin },
        { withCredentials: true }
      );

      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error("Error creating checkout:", error);
      toast.error("Erro ao iniciar pagamento");
      setProcessing(false);
    }
  };

  const handleManageSubscription = async () => {
    setProcessing(true);
    try {
      const response = await axios.post(
        `${API}/subscription/portal`,
        { return_url: window.location.href },
        { withCredentials: true }
      );

      // Redirect to Stripe Customer Portal
      window.location.href = response.data.portal_url;
    } catch (error) {
      console.error("Error opening portal:", error);
      toast.error("Erro ao abrir portal de gestão");
      setProcessing(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("pt-PT", {
      day: "numeric",
      month: "long",
      year: "numeric"
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cockpit-dark flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const isPremium = subscription?.can_access_full && subscription?.plan_type !== "trial";
  const isTrial = subscription?.plan_type === "trial";
  const isExpired = subscription?.plan_type === "expired";

  return (
    <div className="min-h-screen bg-cockpit-dark">
      {/* Header */}
      <header className="bg-slate-900/80 border-b border-slate-800 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/dashboard")}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <h1 className="font-chivo text-xl text-white flex items-center gap-2">
              <Crown className="w-5 h-5 text-yellow-400" />
              Subscrição
            </h1>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Current Status */}
        <Card className="instrument-panel mb-8">
          <CardHeader>
            <CardTitle className="font-chivo text-lg flex items-center gap-2">
              <Clock className="w-5 h-5 text-cyan-400" />
              Estado Atual
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    isPremium ? "bg-yellow-500/20 text-yellow-400 border border-yellow-500/30" :
                    isTrial ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30" :
                    "bg-red-500/20 text-red-400 border border-red-500/30"
                  }`}>
                    {isPremium ? "Premium Ativo" : isTrial ? "Período de Teste" : "Expirado"}
                  </span>
                  {subscription?.plan_type === "admin_granted" && (
                    <span className="px-2 py-0.5 rounded text-xs bg-purple-500/20 text-purple-400">
                      Atribuído pelo Admin
                    </span>
                  )}
                  {subscription?.has_stripe_subscription && (
                    <span className="px-2 py-0.5 rounded text-xs bg-emerald-500/20 text-emerald-400">
                      Renovação Automática
                    </span>
                  )}
                </div>
                <p className="text-slate-400 mt-2">
                  {isPremium && subscription?.subscription_ends_at && (
                    <>
                      {subscription.has_stripe_subscription ? (
                        <>Renova automaticamente em {formatDate(subscription.subscription_ends_at)}</>
                      ) : (
                        <>Válido até {formatDate(subscription.subscription_ends_at)} ({subscription.days_remaining} dias restantes)</>
                      )}
                    </>
                  )}
                  {isTrial && subscription?.trial_ends_at && (
                    <>Teste grátis até {formatDate(subscription.trial_ends_at)} ({subscription.days_remaining} dias restantes)</>
                  )}
                  {isExpired && (
                    <>O seu período de teste terminou. Subscreva para continuar.</>
                  )}
                  {subscription?.plan_type === "admin_granted" && (
                    <>Acesso ilimitado atribuído pelo administrador</>
                  )}
                </p>
              </div>
              {subscription?.can_access_full && (
                <div className="text-right">
                  <Check className="w-8 h-8 text-emerald-400" />
                  <p className="text-xs text-emerald-400 mt-1">Acesso Completo</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Pricing */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Trial Info */}
          <Card className="instrument-panel">
            <CardHeader>
              <CardTitle className="font-chivo text-lg">Teste Grátis</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white mb-4">
                7 dias <span className="text-lg text-slate-400 font-normal">grátis</span>
              </div>
              <ul className="space-y-3 text-sm">
                <li className="flex items-center gap-2 text-slate-300">
                  <Check className="w-4 h-4 text-emerald-400" />
                  Acesso completo a todas as funcionalidades
                </li>
                <li className="flex items-center gap-2 text-slate-300">
                  <Check className="w-4 h-4 text-emerald-400" />
                  Limitado à disciplina de Legislação
                </li>
                <li className="flex items-center gap-2 text-slate-300">
                  <Check className="w-4 h-4 text-emerald-400" />
                  Treinos e exames ilimitados
                </li>
                <li className="flex items-center gap-2 text-slate-300">
                  <Check className="w-4 h-4 text-emerald-400" />
                  Sistema de gamificação completo
                </li>
              </ul>
              {isTrial && (
                <div className="mt-4 p-3 bg-cyan-500/10 rounded-lg border border-cyan-500/20">
                  <p className="text-cyan-400 text-sm">
                    Está a usar o período de teste. Restam {subscription.days_remaining} dias.
                  </p>
                </div>
              )}
              {isExpired && (
                <div className="mt-4 p-3 bg-red-500/10 rounded-lg border border-red-500/20">
                  <p className="text-red-400 text-sm">
                    O seu período de teste expirou. Subscreva para continuar.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Premium Plan */}
          <Card className="instrument-panel border-yellow-500/30 relative overflow-hidden">
            <div className="absolute top-0 right-0 bg-yellow-500 text-black text-xs font-bold px-3 py-1 rounded-bl">
              RECOMENDADO
            </div>
            <CardHeader>
              <CardTitle className="font-chivo text-lg flex items-center gap-2">
                <Crown className="w-5 h-5 text-yellow-400" />
                Premium
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white mb-4">
                €{subscription?.price || "19.99"} <span className="text-lg text-slate-400 font-normal">/ mês</span>
              </div>
              <ul className="space-y-3 text-sm">
                <li className="flex items-center gap-2 text-slate-300">
                  <Plane className="w-4 h-4 text-yellow-400" />
                  Treinos ilimitados
                </li>
                <li className="flex items-center gap-2 text-slate-300">
                  <BookOpen className="w-4 h-4 text-yellow-400" />
                  Todos os cadernos de exame
                </li>
                <li className="flex items-center gap-2 text-slate-300">
                  <Target className="w-4 h-4 text-yellow-400" />
                  Exames simulados ilimitados
                </li>
                <li className="flex items-center gap-2 text-slate-300">
                  <Award className="w-4 h-4 text-yellow-400" />
                  Estatísticas e progresso detalhado
                </li>
              </ul>
              
              {!isPremium && subscription?.plan_type !== "admin_granted" && (
                <Button
                  onClick={handleSubscribe}
                  disabled={processing}
                  className="w-full mt-6 bg-yellow-500 hover:bg-yellow-600 text-black font-bold"
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      A processar...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" />
                      Subscrever Agora
                    </>
                  )}
                </Button>
              )}
              
              {isPremium && (
                <div className="mt-4 p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
                  <p className="text-yellow-400 text-sm flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    Já é membro Premium!
                  </p>
                </div>
              )}
              
              {/* Manage Subscription Button */}
              {subscription?.can_manage_subscription && subscription?.plan_type === "premium" && (
                <Button
                  onClick={handleManageSubscription}
                  disabled={processing}
                  variant="outline"
                  className="w-full mt-4 border-slate-600 hover:border-slate-500"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Gerir Subscrição
                </Button>
              )}
            </CardContent>
          </Card>
        </div>

        {/* FAQ */}
        <Card className="instrument-panel">
          <CardHeader>
            <CardTitle className="font-chivo text-lg">Perguntas Frequentes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-medium text-white mb-1">Posso cancelar a qualquer momento?</h4>
              <p className="text-sm text-slate-400">
                Sim, pode cancelar a sua subscrição a qualquer momento. O acesso continua até ao fim do período pago.
              </p>
            </div>
            <div>
              <h4 className="font-medium text-white mb-1">O que acontece quando o período de teste termina?</h4>
              <p className="text-sm text-slate-400">
                Após os 7 dias de teste, precisará de uma subscrição ativa para continuar a usar a plataforma.
              </p>
            </div>
            <div>
              <h4 className="font-medium text-white mb-1">Que métodos de pagamento aceitam?</h4>
              <p className="text-sm text-slate-400">
                Aceitamos cartões de crédito/débito (Visa, Mastercard, American Express) através do Stripe.
              </p>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default SubscriptionPage;
